import { useCallback, useEffect, useRef, useState } from 'react'
import { SLOW_FACTOR, announce, isFollower, isIdle } from './refreshCoordinator'

// D-VIEWS-AUTOREFRESH (Maya round table, 2026-08-06): the single auto-refresh behaviour shared by
// every view (StationQueue system views + CustomViewTab custom/new views + RefillsTab). Polls every
// 15s, but only while the tab is visible (no background churn), refreshes immediately when the user
// returns to the tab, and never overlaps an in-flight refresh. A small jitter avoids a thundering
// herd across many open tabs. The caller's refresh must be SILENT (swap rows only) so scroll,
// selection, and the client-applied filters/sort are never disturbed.
//
// 86cb8uk5f: background ticks also slow to SLOW_FACTOR x the interval while the tab is idle (no
// input for 5 min) or, for a caller that passes `leaderKey`, while another tab is already polling
// the same key (refreshCoordinator.ts). A user returning to the tab — visibility or focus — always
// refreshes at once, so slowing never leaves the screen someone is looking at stale.

export const VIEW_REFRESH_MS = 15000

export interface AutoRefresh {
  autoOn: boolean
  setAutoOn: (v: boolean) => void
  refreshing: boolean
  lastUpdated: number
  refreshNow: () => void
}

export function useAutoRefresh(
  onRefresh: () => void | Promise<void>,
  opts?: {
    intervalMs?: number
    enabled?: boolean
    /** Cross-tab dedupe key. MUST include the identity the data belongs to (account/pharmacy). */
    leaderKey?: string
    /** Caller-controlled pause (e.g. a page's own "live" toggle); unlike `enabled` it is reactive. */
    active?: boolean
  },
): AutoRefresh {
  const intervalMs = opts?.intervalMs ?? VIEW_REFRESH_MS
  const [autoOn, setAutoOn] = useState(opts?.enabled ?? true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number>(() => Date.now())
  const inFlight = useRef(false)
  const lastRunAt = useRef(Date.now())
  const cbRef = useRef(onRefresh)
  cbRef.current = onRefresh
  const leaderKey = opts?.leaderKey
  const keyRef = useRef(leaderKey)
  keyRef.current = leaderKey
  const active = opts?.active ?? true

  const refreshNow = useCallback(() => {
    if (inFlight.current) return
    inFlight.current = true
    lastRunAt.current = Date.now()
    if (keyRef.current) announce(keyRef.current)
    setRefreshing(true)
    // The callback is invoked INSIDE the promise chain, not as an argument to Promise.resolve().
    // Evaluating it outside meant a synchronous throw escaped before `.finally` was attached,
    // leaving inFlight pinned true and `refreshing` stuck on — auto-refresh silently dead for the
    // life of the mount, with the spinner running. Async callbacks reject rather than throw, so
    // today's callers never hit it; a plain function that throws would kill the view's updates.
    void Promise.resolve()
      .then(() => cbRef.current())
      .then(() => setLastUpdated(Date.now()))
      .catch(() => { /* a failed refresh must not stop the next one; the view keeps its last rows */ })
      .finally(() => {
        inFlight.current = false
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    if (!autoOn || !active) return
    const period = intervalMs + Math.floor(Math.random() * 1500)
    // A leader polls every `period`; past twice that, it has gone (closed, hidden or idle).
    const leaderFreshMs = 2 * period
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      const key = keyRef.current
      const slow = isIdle() || (!!key && isFollower(key, leaderFreshMs))
      // Ticks stay at the base period; a slowed tab simply lets SLOW_FACTOR-1 of them pass.
      if (slow && Date.now() - lastRunAt.current < period * SLOW_FACTOR - 250) return
      refreshNow()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshNow()
    }
    // Focus without a visibility change: a second window on another monitor being clicked into.
    const onFocus = () => {
      if (Date.now() - lastRunAt.current >= intervalMs) refreshNow()
    }
    const id = window.setInterval(tick, period)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
    }
  }, [autoOn, active, intervalMs, refreshNow])

  return { autoOn, setAutoOn, refreshing, lastUpdated, refreshNow }
}
